class ArchiveTheme {
  constructor() {
    this.checkoutInProgress = false;
    this.cartHasCortex = false;
    this.pendingCortexCart = null;
    this.lastModalTrigger = null;
    this.initPageTransition();
    document.addEventListener('submit', this.handleSubmit.bind(this));
    document.addEventListener('submit', this.handleCheckoutSubmit.bind(this));
    document.addEventListener('submit', this.handleCortexSubmit.bind(this));
    document.addEventListener('change', this.handleVariantChange.bind(this));
    document.addEventListener('click', this.handleCartPageClick.bind(this));
    document.addEventListener('click', this.handleCartPopupClick.bind(this));
    document.addEventListener('click', this.handleCheckoutClick.bind(this));
    document.addEventListener('click', this.handleQuickAddClick.bind(this));
    document.addEventListener('keydown', this.handleModalKeydown.bind(this));
    document.addEventListener('archive:cart-refresh-count', this.syncCartCount.bind(this));
    document.addEventListener('archive:cart-updated', this.pulseCartBar.bind(this));
    this.syncCartCount();
  }

  initPageTransition() {
    const html = document.documentElement;
    const body = document.body;
    const transitionLayer = document.querySelector('[data-page-transition-layer]');
    if (!transitionLayer) {
      html.classList.remove('archive-nav-pending');
      return;
    }

    let shouldAnimate = false;
    try {
      shouldAnimate = sessionStorage.getItem('archive:splash-transition') === '1';
    } catch (_error) {
      shouldAnimate = false;
    }

    if (!shouldAnimate) {
      html.classList.remove('archive-nav-pending');
      return;
    }

    try {
      sessionStorage.removeItem('archive:splash-transition');
    } catch (_error) {
    }

    body.classList.add('archive-nav-entering');
    window.requestAnimationFrame(() => {
      html.classList.remove('archive-nav-pending');
    });

    window.setTimeout(() => {
      body.classList.remove('archive-nav-entering');
      html.classList.remove('archive-nav-pending');
    }, 760);
  }

  formatMoney(cents) {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents);
    }

    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD'
    }).format((cents || 0) / 100);
  }

  async fetchCart() {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart.js`, {
      headers: { 'Accept': 'application/json' }
    });
    if (!response.ok) throw new Error('Unable to load cart');
    return response.json();
  }

  renderCartPopup(cart) {
    const modal = document.querySelector('[data-cart-popup]');
    const itemsRoot = modal?.querySelector('[data-cart-popup-items]');
    if (!modal || !itemsRoot) return;

    itemsRoot.replaceChildren();

    cart.items.forEach((item, index) => {
      const row = document.createElement('article');
      row.className = 'archive-cart-popup__item';

      const imageFrame = document.createElement('div');
      imageFrame.className = 'archive-cart-popup__image';
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.product_title || item.title || '';
        image.loading = 'lazy';
        imageFrame.appendChild(image);
      }

      const meta = document.createElement('div');
      meta.className = 'archive-cart-popup__meta';
      const title = document.createElement('span');
      title.className = 'archive-cart-popup__title';
      const fullTitle = item.product_title || item.title || '';
      title.textContent = fullTitle.split('|')[0].trim().replace(/_/g, ' ');
      const creatorName = (item.vendor || fullTitle.split('|')[1] || '').trim();
      const creator = document.createElement('span');
      creator.className = 'archive-cart-popup__creator';
      creator.textContent = creatorName ? `by ${creatorName}` : '';
      creator.hidden = !creatorName;
      const price = document.createElement('span');
      price.className = 'archive-cart-popup__price';
      price.textContent = this.formatMoney(item.final_line_price);
      meta.append(title, creator, price);

      const controls = document.createElement('div');
      controls.className = 'archive-cart-popup__controls';
      const quantity = document.createElement('div');
      quantity.className = 'archive-cart-popup__qty';
      quantity.setAttribute('aria-label', 'Quantity');

      const decrease = document.createElement('button');
      decrease.type = 'button';
      decrease.dataset.cartPopupLine = String(index + 1);
      decrease.dataset.cartPopupQuantity = String(Math.max(0, item.quantity - 1));
      decrease.setAttribute('aria-label', 'Decrease quantity');
      decrease.textContent = '−';

      const count = document.createElement('span');
      count.textContent = String(item.quantity);

      const increase = document.createElement('button');
      increase.type = 'button';
      increase.dataset.cartPopupLine = String(index + 1);
      increase.dataset.cartPopupQuantity = String(item.quantity + 1);
      increase.setAttribute('aria-label', 'Increase quantity');
      increase.textContent = '+';
      quantity.append(decrease, count, increase);

      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'archive-cart-popup__remove';
      remove.dataset.cartPopupLine = String(index + 1);
      remove.dataset.cartPopupQuantity = '0';
      remove.textContent = 'remove';
      controls.append(quantity, remove);
      row.append(imageFrame, meta, controls);
      itemsRoot.appendChild(row);
    });

    modal.querySelectorAll('[data-cart-popup-count]').forEach((node) => {
      node.textContent = String(cart.item_count);
    });
    const total = modal.querySelector('[data-cart-popup-total]');
    if (total) total.textContent = this.formatMoney(cart.total_price);
    const isEmpty = cart.item_count <= 0;
    const empty = modal.querySelector('[data-cart-popup-empty]');
    const footer = modal.querySelector('[data-cart-popup-footer]');
    if (empty) empty.hidden = !isEmpty;
    if (footer) footer.hidden = isEmpty;
  }

  async openCartPopup(trigger) {
    const modal = document.querySelector('[data-cart-popup]');
    if (!modal) return;

    this.lastModalTrigger = trigger || document.activeElement;
    try {
      const cart = await this.fetchCart();
      this.renderCartPopup(cart);
      modal.hidden = false;
      document.body.classList.add('archive-modal-open');
      modal.querySelector('.archive-modal__panel')?.focus();
    } catch (_error) {
      window.location.href = window.Shopify?.routes?.cart_url || '/cart';
    }
  }

  closeCartPopup() {
    const modal = document.querySelector('[data-cart-popup]');
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    if (document.querySelector('[data-cortex-checkout]')?.hidden !== false) {
      document.body.classList.remove('archive-modal-open');
    }
    this.lastModalTrigger?.focus?.();
  }

  handleCartPopupClick(event) {
    const openTrigger = event.target.closest('[data-cart-popup-trigger]');
    if (openTrigger) {
      event.preventDefault();
      this.openCartPopup(openTrigger);
      return;
    }

    if (event.target.closest('[data-cart-popup-close]')) {
      event.preventDefault();
      this.closeCartPopup();
      return;
    }

    const lineTrigger = event.target.closest('[data-cart-popup-line]');
    if (!lineTrigger) return;
    event.preventDefault();
    const line = Number(lineTrigger.dataset.cartPopupLine);
    const quantity = Number(lineTrigger.dataset.cartPopupQuantity);
    if (!line || Number.isNaN(quantity)) return;
    this.updateCartLine(line, Math.max(0, quantity));
  }

  handleVariantChange(event) {
    const selector = event.target.closest('[data-variant-selector]');
    if (!selector) return;

    const form = selector.closest('[data-product-form]');
    const variantJson = form?.querySelector('[data-product-variants]');
    if (!form || !variantJson) return;

    const variants = JSON.parse(variantJson.textContent);
    const variant = variants.find((item) => String(item.id) === selector.value);
    if (!variant) return;

    const idInput = form.querySelector('input[name="id"]');
    const price = form.closest('[data-product-detail]')?.querySelector('[data-product-price]');
    const compare = form.closest('[data-product-detail]')?.querySelector('[data-product-compare-price]');

    if (idInput) idInput.value = variant.id;
    if (price) price.textContent = this.formatMoney(variant.price);
    if (compare) {
      compare.textContent = variant.compare_at_price ? this.formatMoney(variant.compare_at_price) : '';
      compare.classList.toggle('hidden', !variant.compare_at_price);
    }

    const nextUrl = new URL(window.location.href);
    nextUrl.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', nextUrl);
  }

  updateCartCountIndicators(count) {
    const countIndicators = document.querySelectorAll('[data-cart-count]');
    if (!countIndicators.length) return;

    countIndicators.forEach((indicator) => {
      indicator.textContent = String(count);
      indicator.classList.toggle('is-hidden', count <= 0);
    });

    // The bottom cart bar only exists while the cart has items.
    document.querySelectorAll('[data-cart-bar]').forEach((bar) => {
      bar.classList.toggle('is-hidden', count <= 0);
    });
  }

  // Quick bounce on the bottom bar so an add-to-cart visibly lands there.
  pulseCartBar() {
    document.querySelectorAll('[data-cart-bar]').forEach((bar) => {
      bar.classList.remove('is-pulsing');
      void bar.offsetWidth;
      bar.classList.add('is-pulsing');
      window.setTimeout(() => bar.classList.remove('is-pulsing'), 500);
    });
  }

  async syncCartCount() {
    const countIndicators = document.querySelectorAll('[data-cart-count]');
    if (!countIndicators.length) return;

    const root = window.Shopify?.routes?.root || '/';

    try {
      const cart = await this.fetchCart();
      this.syncCortexCartState(cart);
      this.updateCartCountIndicators(Number(cart.item_count) || 0);
      if (document.querySelector('[data-cart-popup]')?.hidden === false) {
        this.renderCartPopup(cart);
      }
    } catch (_error) {
      // Keep UI stable if cart endpoint is temporarily unavailable.
    }
  }

  async renderMainCartSection() {
    const currentRoot = document.querySelector('[data-main-cart-root]');
    if (!currentRoot) return;

    const root = window.Shopify?.routes?.root || '/';

    try {
      const response = await fetch(`${root}cart?sections=main-cart`, {
        headers: { 'Accept': 'application/json' }
      });
      if (!response.ok) throw new Error('Unable to load cart section');

      const payload = await response.json();
      const wrapper = document.createElement('div');
      wrapper.innerHTML = payload['main-cart'];
      const nextRoot = wrapper.querySelector('[data-main-cart-root]');
      if (!nextRoot) throw new Error('Missing cart markup');

      currentRoot.replaceWith(nextRoot);
    } catch (_error) {
      window.location.reload();
    }
  }

  async updateCartLine(line, quantity) {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({ line, quantity })
    });

    if (!response.ok) return;
    const cart = await response.json();

    this.syncCortexCartState(cart);
    await this.renderMainCartSection();
    this.renderCartPopup(cart);
    this.updateCartCountIndicators(Number(cart.item_count) || 0);
    document.dispatchEvent(new CustomEvent('archive:cart-refresh-count'));
    document.dispatchEvent(new CustomEvent('archive:cart-updated'));
  }

  handleCartPageClick(event) {
    const quantityTrigger = event.target.closest('[data-cart-page-quantity]');
    const removeTrigger = event.target.closest('[data-cart-page-remove]');
    if (!quantityTrigger && !removeTrigger) return;

    const cartPage = event.target.closest('[data-main-cart]');
    if (!cartPage) return;

    event.preventDefault();
    const line = Number((quantityTrigger || removeTrigger).dataset.line);
    const quantity = removeTrigger ? 0 : Number(quantityTrigger.dataset.quantity);
    if (!line || Number.isNaN(quantity)) return;

    this.updateCartLine(line, Math.max(0, quantity));
  }

  async handleQuickAddClick(event) {
    const trigger = event.target.closest('[data-quick-add]');
    if (!trigger) return;

    event.preventDefault();
    event.stopPropagation();

    const variantId = Number(trigger.dataset.variantId);
    if (!variantId || trigger.disabled) return;

    trigger.disabled = true;
    const root = window.Shopify?.routes?.root || '/';

    try {
      const formData = new FormData();
      formData.append('id', String(variantId));
      formData.append('quantity', '1');
      if (trigger.dataset.cortexDelivery === 'true') {
        formData.append('properties[_archive_delivery]', 'cortex_cloud');
      }

      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Accept': 'application/json' },
        body: formData
      });
      if (!response.ok) return;

      if (document.querySelector('[data-main-cart-root]')) {
        await this.renderMainCartSection();
      }

      await this.syncCartCount();
      document.dispatchEvent(new CustomEvent('archive:cart-refresh-count'));
      document.dispatchEvent(new CustomEvent('archive:cart-updated'));

      // Fill the cart icon briefly so the click visibly registered.
      trigger.classList.add('is-added');
      window.setTimeout(() => trigger.classList.remove('is-added'), 900);
    } finally {
      trigger.disabled = false;
    }
  }

  async handleSubmit(event) {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;

    const submitter = event.submitter;
    const shouldCheckout = submitter?.hasAttribute('data-product-checkout');
    const shouldUseDrawer = document.body.classList.contains('cart-type-drawer');
    if (!shouldUseDrawer && !shouldCheckout) return;

    event.preventDefault();
    const formData = new FormData(form);
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Accept': 'application/json'
      },
      body: formData
    });

    if (!response.ok) return;

    if (shouldCheckout) {
      await this.beginCheckout();
      return;
    }

    await this.syncCartCount();
    document.dispatchEvent(new CustomEvent('archive:cart-updated'));
  }

  handleCheckoutClick(event) {
    const close = event.target.closest('[data-cortex-close]');
    if (close) {
      event.preventDefault();
      this.closeCortexDialog();
      return;
    }

    const trigger = event.target.closest('[data-checkout-trigger]');
    if (!trigger) return;
    event.preventDefault();
    this.beginCheckout();
  }

  handleCheckoutSubmit(event) {
    const form = event.target.closest('[data-checkout-form]');
    if (!form) return;
    event.preventDefault();
    this.beginCheckout();
  }

  isCortexItem(item) {
    const properties = item.properties || {};
    const title = item.product_title || item.title || '';
    return properties._archive_delivery === 'cortex_cloud'
      || Object.prototype.hasOwnProperty.call(properties, 'Cortex Cloud username')
      || /^(QC|Quad Cortex)\b/i.test(title);
  }

  syncCortexCartState(cart) {
    this.cartHasCortex = Array.isArray(cart?.items) && cart.items.some((item) => this.isCortexItem(item));
    document.documentElement.classList.toggle('cart-has-cortex', this.cartHasCortex);
  }

  openCortexDialog(cart, cortexItems) {
    const modal = document.querySelector('[data-cortex-checkout]');
    if (!modal) return;

    this.pendingCortexCart = cart;
    const input = modal.querySelector('[data-cortex-checkout-username]');
    const summary = modal.querySelector('[data-cortex-item-summary]');
    const status = modal.querySelector('[data-cortex-status]');
    if (input) input.value = '';
    if (summary) {
      const count = cortexItems.reduce((total, { item }) => total + item.quantity, 0);
      summary.textContent = `${count} Quad Cortex ${count === 1 ? 'item' : 'items'} in this order`;
    }
    if (status) status.textContent = '';
    modal.hidden = false;
    document.body.classList.add('archive-modal-open');
    window.setTimeout(() => input?.focus(), 0);
  }

  closeCortexDialog() {
    const modal = document.querySelector('[data-cortex-checkout]');
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    this.checkoutInProgress = false;
    this.pendingCortexCart = null;
    if (document.querySelector('[data-cart-popup]')?.hidden !== false) {
      document.body.classList.remove('archive-modal-open');
    }
  }

  handleModalKeydown(event) {
    if (event.key !== 'Escape') return;
    if (document.querySelector('[data-cortex-checkout]')?.hidden === false) {
      this.closeCortexDialog();
      return;
    }
    if (document.querySelector('[data-cart-popup]')?.hidden === false) {
      this.closeCartPopup();
    }
  }

  async beginCheckout() {
    if (this.checkoutInProgress) return;
    this.checkoutInProgress = true;
    const root = window.Shopify?.routes?.root || '/';

    // Mobile networks and privacy browsers can delay a fresh /cart.js request.
    // syncCartCount already keeps this state current, so open the required
    // username step immediately instead of making the checkout tap wait.
    if (this.cartHasCortex || document.documentElement.classList.contains('cart-has-cortex')) {
      this.openCortexDialog(null, []);
      return;
    }

    try {
      const cart = await this.fetchCart();
      this.syncCortexCartState(cart);
      const cortexItems = cart.items
        .map((item, index) => ({ item, line: index + 1 }))
        .filter(({ item }) => this.isCortexItem(item));

      if (!cortexItems.length) {
        window.location.href = `${root}checkout`;
        return;
      }

      this.openCortexDialog(cart, cortexItems);
    } catch (_error) {
      this.checkoutInProgress = false;
    }
  }

  async handleCortexSubmit(event) {
    const form = event.target.closest('[data-cortex-form]');
    if (!form) return false;
    event.preventDefault();

    const input = form.querySelector('[data-cortex-checkout-username]');
    const status = form.querySelector('[data-cortex-status]');
    const button = form.querySelector('[data-cortex-continue]');
    const username = input?.value.trim() || '';
    if (!username) {
      input?.setCustomValidity('Enter your Cortex Cloud username.');
      input?.reportValidity();
      input?.focus();
      return true;
    }
    input.setCustomValidity('');

    const cart = this.pendingCortexCart || await this.fetchCart();
    const lines = cart.items
      .map((item, index) => ({ item, line: index + 1 }))
      .filter(({ item }) => this.isCortexItem(item))
      .sort((a, b) => b.line - a.line);

    button.disabled = true;
    if (status) status.textContent = 'saving delivery details…';
    const root = window.Shopify?.routes?.root || '/';

    try {
      for (const { item, line } of lines) {
        const properties = { ...(item.properties || {}) };
        properties._archive_delivery = 'cortex_cloud';
        properties['Cortex Cloud username'] = username;
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ line, quantity: item.quantity, properties })
        });
        if (!response.ok) throw new Error('Unable to save delivery details');
      }
      window.location.href = `${root}checkout`;
    } catch (_error) {
      if (status) status.textContent = 'We couldn’t save that. Please try again.';
      button.disabled = false;
      this.checkoutInProgress = false;
    }
    return true;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new ArchiveTheme();
});

// Product title band (mobile): shrink the font until the title fits one line.
document.addEventListener('DOMContentLoaded', () => {
  const title = document.querySelector('.product-detail__crumb-product');
  if (!title) return;
  const fit = () => {
    title.style.fontSize = '';
    if (window.innerWidth > 749) return;
    let size = parseFloat(getComputedStyle(title).fontSize);
    let guard = 60;
    while (guard-- > 0 && title.scrollWidth > title.clientWidth && size > 14) {
      size -= 1;
      title.style.fontSize = size + 'px';
    }
  };
  fit();
  window.addEventListener('resize', fit);
});

// Collection row scroll UI: right-edge blur + next-arrow while more products
// remain to the right, and pager squares that track which products are visible.
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-collection-main] .collection-row').forEach((row, rowIndex) => {
    const scroller = row.querySelector('.collection-row__scroll');
    if (!scroller) return;
    const cards = [...row.querySelectorAll('.product-card--catalog:not(.coming-soon-card)')];
    const dots = [...row.querySelectorAll('.collection-row__pager-dot')];
    const nextBtn = row.querySelector('.collection-row__next');
    const prevBtn = row.querySelector('.collection-row__prev');

    const update = () => {
      const remaining = scroller.scrollWidth - scroller.clientWidth - scroller.scrollLeft;
      row.classList.toggle('collection-row--more', remaining > 24);
      row.classList.toggle('collection-row--back', scroller.scrollLeft > 24);
      const viewStart = scroller.scrollLeft;
      const viewEnd = viewStart + scroller.clientWidth;
      dots.forEach((dot) => {
        const card = cards[Number(dot.dataset.pagerIndex)];
        if (!card) return;
        const overlap = Math.min(viewEnd, card.offsetLeft + card.offsetWidth) - Math.max(viewStart, card.offsetLeft);
        dot.classList.toggle('is-visible', overlap >= card.offsetWidth * 0.5);
      });
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const card = cards[Number(dot.dataset.pagerIndex)];
        if (card) scroller.scrollTo({ left: card.offsetLeft, behavior: 'auto' });
      });
    });

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        const next = cards.find((card) => card.offsetLeft > scroller.scrollLeft + 4);
        if (next) scroller.scrollTo({ left: next.offsetLeft, behavior: 'smooth' });
      });
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        const prev = [...cards].reverse().find((card) => card.offsetLeft < scroller.scrollLeft - 4);
        scroller.scrollTo({ left: prev ? prev.offsetLeft : 0, behavior: 'smooth' });
      });
    }

    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();

    // Auto-advance to the next product while the row is on screen and idle.
    // Pauses on hover / touch, loops back to the start, staggered per row.
    if (cards.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      let paused = false;
      let rowInView = false;

      new IntersectionObserver((entries) => {
        rowInView = entries[0].isIntersecting;
      }, { threshold: 0.4 }).observe(row);

      row.addEventListener('mouseenter', () => { paused = true; });
      row.addEventListener('mouseleave', () => { paused = false; });
      // Keep the row still while a version dropdown is open/focused.
      row.addEventListener('focusin', () => { paused = true; });
      row.addEventListener('focusout', () => { paused = false; });
      row.addEventListener('touchstart', () => {
        paused = true;
        setTimeout(() => { paused = false; }, 8000);
      }, { passive: true });

      const advance = () => {
        if (paused || !rowInView || document.hidden) return;
        const maxLeft = scroller.scrollWidth - scroller.clientWidth;
        if (maxLeft <= 8) return;
        if (scroller.scrollLeft >= maxLeft - 8) {
          scroller.scrollTo({ left: 0, behavior: 'smooth' });
          return;
        }
        const next = cards.find((card) => card.offsetLeft > scroller.scrollLeft + 4);
        scroller.scrollTo({ left: next ? Math.min(next.offsetLeft, maxLeft) : 0, behavior: 'smooth' });
      };

      setTimeout(() => {
        setInterval(advance, 6000);
      }, 1500 * rowIndex);
    }
  });
});
